import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';
import MaterialTable from "@material-table/core";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import {
  IconButton,
  TablePagination,
  useTheme,
} from "@material-ui/core";
import { DatePicker } from '@material-ui/pickers';
import {AttachMoney, Email, List, MonetizationOn} from "@material-ui/icons";
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import HighQualityIcon from '@mui/icons-material/HighQuality';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import {Preview, PriceChange} from "@mui/icons-material";
import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import { ColumnWidth } from "../../../blocks/ColumnHelper";
import Icons, { AirplaneEditIcon } from '../../../icons';
import {ComboSelect, MaskedText, MaskedNumber, FormSelect, IconButtonMenu} from '../../../controls';
import EditEnquiryDialog from "./EditEnquiryDialog";
import AddEnquiryDialog from "./AddEnquiryDialog";
import useTripDepartmentUsers from '../../../services/auth/useTripDepartmentUsers';
import EnquiryActivityList from "./EnquiryActivityList";
import AddCostingDialog from '../../Costings/components/AddCostingDialog';
import { useEnquiries, useAddEnquiry, useUpdateEnquiry, useDeleteEnquiry } from "../../../services/enquiry";
import { useAddQuoteFromEnquiry } from "../../../services/quotes";
import { useAddCosting, useUpdateCosting } from '../../../services/costings';
import { useComboValues } from '../../../services/settings';
import EditCostingDialog from "../../Costings/components/EditCostingDialog";
import ViewCostingDialog from "../../Costings/components/ViewCostingDialog";
import TemplateSelectionDialog from "./TemplateSelectionDialog";
import ViewQuoteDialog from "./ViewQuoteDialog";
import AttachCostingDialog from './AttachCostingDialog';
import GenerateTripDialog from './GenerateTripDialog';

const now = moment.utc();
const initialDate = now.toISOString();
const dateFormat = "DD/MM/YYYY";

const apiSitePrefix = getApiSitePrefix();
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}

const EnquiriesList = (_props) => {
  const props = {
    ..._props
  }
  const {classes, costingClasses} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const { user } = useAuth0();

  const [state] = useState({
    canEdit: props.canEdit,
  });
  const [selectTemplate, setSelectTemplate] = useState({
    open: false,
    template: null,
    record: null
  });

  const [defaultValues, setDefaultValues] = useState({});
  const [costingDefaultValues, setCostingDefaultValues] = useState({});

  const year = props.year;
  const month = props.month;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddEnquiry = useAddEnquiry();
  const handleUpdateEnquiry = useUpdateEnquiry();
  const handleDeleteEnquiry = useDeleteEnquiry();

  const handleAddCosting = useAddCosting();
  const handleUpdateCosting = useUpdateCosting();
  const handleAddQuote = useAddQuoteFromEnquiry();

  const queryClient = useQueryClient();
  const extraKeys = [year, month];

  const enquiries = useEnquiries({tableRef, queryClient, extraKeys});
  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [defaultValues?.ownerDepartment]});

  const statuses = useComboValues({queryClient, extraKeys: ["ENQUIRIES", "STATUS"]});
  const activityStatuses = useComboValues({queryClient, extraKeys: ["ENQUIRIES", "ACTIVITY STATUS"]});
  const qualificationOptions = useComboValues({queryClient, extraKeys: ["ENQUIRIES", "QUALIFICATION"]});
  const uldTypes = useComboValues({queryClient, extraKeys: ["ULD TYPES"], useCache: true});

  const userOptions = departmentUsers.data?departmentUsers.data.map((userOption, i) => {
    return {action: userOption.user_id, itemLabel: `${userOption.firstName} ${userOption.lastName}`};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const changeStatusOptions = statuses.data?statuses.data.map((status, i) => {
    return {action: status.code, itemLabel: status.value};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const changeQualificationOptions = qualificationOptions.data?qualificationOptions.data.map((status, i) => {
    return {action: status.code, itemLabel: status.value};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const getMaxPayload = async (aircraftReg, origin, destination) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/aircraft/getMaxPayload/${aircraftReg}/${origin}/${destination}`);
      if (response && response.status && response.status === 200 && response.data) {
        return response.data;
      }

      if (!response.status) {
        const message = `Could not retrieve Max Payload. Server is not responding`;
        console.log(message);
        SystemData.alert("Error", message);
      } else if (!response.data) {
        const message = `Could not retrieve Max Payload. Server returned ${response.status}`;
        console.log(message);
        SystemData.alert("Error", message);
      }
    } catch (err) {
      const message = `Could not retrieve Max Payload. Server is not responding`;
      console.log(message);
      SystemData.alert("Error", message);
    }
  }

  const getQuoteTerms = async (terms, quoteType) => {
    let quoteTerms = [];
    let message = "";
    const termsDescription = terms === "QUOTE EXCLUSIONS" ? "quote exclusions" : "subject to items";
    const termProperty = terms === "QUOTE EXCLUSIONS" ? "excluded" : "subjectTo";
    try {
      let response = await axios.get(`${apiSitePrefix}/combovalues/list/${terms}/${quoteType}`);
      if (response && response.data && Array.isArray(response.data)) {
        response.data.forEach(item => {
          const term = {};
          term[termProperty] = item.value;
          quoteTerms.push(term);
        });
      }
      if (!response.data) {
        message = `Could not retrieve ${termsDescription}. Server is not responding`;
        console.log(message);
        SystemData.alert("Error", message);
      }
    } catch (err) {
      message = `Could not retrieve ${termsDescription}. Server is not responding`
      console.log(message);
      SystemData.alert("Error", message);
    }

    return quoteTerms
  }

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/enquiries/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Enquiries Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Enquiries Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Enquiries Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Enquiries Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Enquiries Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Enquiries Defaults. Server is not responding`);
    }
  }
  if (!defaultValues || __.isEmpty(defaultValues)) getDefaultValues();

  const getCostingDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/costings/defaults`);
      if (response.data && response.data.success) {
        setCostingDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Defaults. Server is not responding`);
    }
  }

  const calculateUldPayload = (payloadLoose, bupCount) => {
    let payloadUld = 0;
    if (uldTypes && uldTypes.data && Array.isArray(uldTypes.data)) {
      const pmcType = __.find(uldTypes?.data||[], {code: 'PMC'});
      let pmcTareWeight = 0;
      if (pmcType) {
        pmcTareWeight = pmcType?.data?.tareWeight;
      }
      const loosePalletWeight = payloadLoose * 0.05;
      let palletCount = Math.ceil(loosePalletWeight / pmcTareWeight) + bupCount;
      if (palletCount > 39) {
        palletCount = 39;
      }

      payloadUld = (palletCount * pmcTareWeight);
    }
    return payloadUld;
  }

  if (!costingDefaultValues || __.isEmpty(costingDefaultValues))
    getCostingDefaultValues();

  const refreshList = async () => {
    queryClient.invalidateQueries(["Enquiries"]);
  }

  const handleNewEnquiry = async () => {
    const newStatus = __.find(activityStatuses.data, {code: "NEW"});
    let nextActionDate = moment.utc().add(8, "hours").toISOString();
    let nextActivity = 'REVIEW';
    if (newStatus && newStatus.data && newStatus.data.nextActivity && newStatus.data.nextActivityInterval && newStatus.data.nextActivityIntervalUnits) {
      nextActionDate = moment.utc().add(newStatus.data.nextActivityInterval, newStatus.data.nextActivityIntervalUnits).toISOString();
      nextActivity = newStatus.data.nextActivity;
    }
    const newEnquiryTemplate = {
      enquiryUuid: uuidv4(),
      enquirySource: "INTERNAL",
      enquiryDate: moment.utc().toISOString(),
      enquiryInfo: null,
      companyName: null,
      companyUuid: null,
      contactName: null,
      contactEmail: null,
      contactPhone: null,
      // originAirportCode: null,
      // destinationAirportCode: null,
      // payload: null,
      // payloadUom: "TONNE",
      cargoAvailability: null,
      requiredAtDestination: null,
      orderFirmness: 0,
      accountManagerId: null,
      status: "NEW",
      nextActionDate,
      nextActivity,
      respondBy: nextActionDate,
      cargo: []
    }
    eventBus.trigger("AddEnquiryDialog.open", {...newEnquiryTemplate})
  }

  const updateEnquiryRecord = (rowData, updateFlag) => {
    switch (updateFlag) {
      case "Assign Account Manager":
        rowData["accountManagerId"] = "true";
        break;
      default:
        break;
    }
    new Promise((resolve, reject) => {
      handleUpdateEnquiry.mutate({record: rowData, oldRecord: rowData, resolve, reject, extraKeys});
    }).catch((error) => {console.log("it's bad") /* TODO: create an alert */});
  }

  const listActionOptions = [
    {action: "Assign Account Manager", itemLabel: "Assign Account Manager", itemIcon: <PersonAddAlt1Icon/>, historySuffix: "", itemMenu: userOptions, disabledStatuses: []},
    {action: "Generate New Costing", itemLabel: "Generate New Costing", itemIcon: <MonetizationOn/>, historySuffix: "", disabledStatuses: []},
    {action: "Associate Costing", itemLabel: "Associate Costing", itemIcon: <AttachMoney/>, historySuffix: "", disabledStatuses: []},
    {action: "View Current Costing", itemLabel: "View Current Costing", itemIcon: <Preview/>, historySuffix: "", prerequisite: "associatedCostingUuid", disabledStatuses: []},
    {action: "Edit Current Costing", itemLabel: "Edit Current Costing", itemIcon: <PriceChange/>, historySuffix: "", prerequisite: "associatedCostingUuid", disabledStatuses: []},
    {action: "Generate Trip", itemLabel: "Generate Trip", itemIcon: <AirplaneEditIcon/>, historySuffix: "", prerequisite: "associatedCostingUuid", disabledStatuses: []},
    {action: "Generate Quote", itemLabel: "Generate Quote", itemIcon: <RequestQuoteIcon/>, historySuffix: "", prerequisite: "associatedCostingUuid", disabledStatuses: ["QUOTED"]},
    {action: "Resend Quote", itemLabel: "Resend Quote", itemIcon: <Email/>, historySuffix: "", prerequisite: "associatedCostingUuid", disabledStatuses:
      [
        "NEW", "MORE INFO", "FOLLOW-UP", "ACCEPTED", "SALE", "INTERNAL", "EXTERNAL","REJECTED", "NO RESPONSE", "CLOSED", "NOT VIABLE"
      ]
    },
    // {action: "Generate Contract", itemLabel: "Generate Contract", itemIcon: <HandshakeIcon/>, historySuffix: "", prerequisite: "associatedQuoteUuid", disabledStatuses: [
    //   "NEW", "MORE INFO", "FOLLOW-UP", "ACCEPTED", "SALE", "INTERNAL", "EXTERNAL","REJECTED", "NO RESPONSE", "CLOSED", "NOT VIABLE"
    // ]},
    // {action: "Generate Contract with Fuel Annex", itemLabel: "Generate Contract with Fuel Annex", itemIcon: <HandshakeIcon/>, historySuffix: "", prerequisite: "associatedQuoteUuid", disabledStatuses: [
    //   "NEW", "MORE INFO", "FOLLOW-UP", "ACCEPTED", "SALE", "INTERNAL", "EXTERNAL","REJECTED", "NO RESPONSE", "CLOSED", "NOT VIABLE"
    // ]},
    {action: "Follow-up Email", itemLabel: "Follow-up Email", itemIcon: <ScheduleSendIcon/>, historySuffix: "", disabledStatuses: []},
    {action: "Further Qualification", itemLabel: "Further Qualification", itemIcon: <HighQualityIcon/>, itemMenu: changeQualificationOptions, historySuffix: "", disabledStatuses: []},
    {action: "Change Status", itemLabel: "Change Status", itemIcon: <AutorenewIcon/>, historySuffix: "", itemMenu: changeStatusOptions, disabledStatuses: []},
  ];

  const emptyCosting = {
    costingDate: moment.utc().toISOString(),
    routeWinds: process.env.REACT_APP_DEFAULT_WINDCODE || 'GRIB-UKO-WX',
    routeWindsMonth: "",
    legs:[],
    cargo: [],
    services: [],
    weightUom: "KG",
    contingencyFuelCostRate:0,
    contingencyBlockHoursRate:0,
  }

  const getCosting = async (costingUuid) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/costings/list/${costingUuid}`);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }

      if (!response.data) {
        const message = `Could not retrieve Costing. Server is not responding`;
        console.log(message);
        SystemData.alert("Error", message);
      } else if (!Array.isArray(response.data) || response.data.length < 1) {
        const message = `Could not retrieve Costing. Costing data not found`;
        console.log(message);
        SystemData.alert("Error", message);
      }
    } catch (err) {
      const message = `Could not retrieve Costing. Server is not responding`;
      console.log(message);
      SystemData.alert("Error", message);
    }

    return null;
  }

  const attachCosting = async (record) => {
    const attachCostingRecord = {
      enquiryUuid: record.enquiryUuid
    }
    eventBus.trigger('AttachCostingDialog.open', attachCostingRecord);
  }

  const generateTrip = async (record) => {
    const scratchPadUser = user.sub;
    const scratchPad = 0;
    const generateTripRecord = {
      enquiryUuid: record.enquiryUuid,
      scratchPadUser,
      scratchPad,
      costingUuid: record.associatedCostingUuid,
      aircraftReg: null,
      airlineCode: null,
      tripDate: moment(record.cargoAvailability).startOf('day').toISOString(),
      initialTime: '12:00'
    }
    eventBus.trigger('GenerateTripDialog.open', generateTripRecord);
  }

  const generateContract = async (record, generateFuelAnnex=false) => {
  //   const {
  //     enquiryUuid,
  //     companyUuid,
  //     contactName,
  //     contactEmail,
  //     contactPhone,
  //     associatedCostingUuid,
  //     associatedQuoteUuid,
  //     accountManagerId,
  //   } = record;

  //   const quoteRecord = await fetchQuotes({queryClient, extraKeys: [associatedQuoteUuid], useCache: true});
  //   console.log(quoteRecord);

  //   // const contractRecord = {
  //   //   contractName
  //   //   contractType
  //   //   contractRevision: 1
  //   //   contractedPartyUuid: companyUuid,
  //   //   contractReference,
  //   //   contractStartDate,
  //   //   contractEndDate,
  //   //   associatedQuoteUuid,
  //   // }

  }

  const generateCosting = async (record) => {
    const {companyName, companyUuid} = record;
    const cargo = __.cloneDeep(record.cargo);
    for (const consignment of cargo) {
      const maxPayload = await getMaxPayload(costingDefaultValues.defaultAircraft, consignment.originAirportCode, consignment.destinationAirportCode);
      const payloadLoose = !isNaN(Number(consignment.payloadLoose)) ? Number(consignment.payloadLoose) : 0;
      const payloadUnitised = !isNaN(Number(consignment.payloadUnitised)) ? Number(consignment.payloadUnitised) : 0;
      const requestedPayload = payloadLoose + payloadUnitised;
      const uldPayload = calculateUldPayload(payloadLoose, consignment.bupCount);
      consignment.payloadUld = uldPayload;
      if ((requestedPayload + uldPayload) > maxPayload) {
        const looseRatio = payloadLoose / requestedPayload;
        const unitisedRatio = payloadUnitised / requestedPayload;
        consignment.payloadLoose = (maxPayload - uldPayload) * looseRatio;
        consignment.payloadUnitised = (maxPayload - uldPayload) * unitisedRatio;
      }
      consignment['companyName'] = companyName;
      consignment['companyUuid'] = companyUuid;
    }

    cargo.forEach(consignment => {
      consignment['companyName'] = companyName;
      consignment['companyUuid'] = companyUuid;
    })

    if (!__.isNil(costingDefaultValues?.contingencyFuelCostRate)) emptyCosting['contingencyFuelCostRate'] = costingDefaultValues.contingencyFuelCostRate;
    if (!__.isNil(costingDefaultValues?.contingencyBlockHoursRate)) emptyCosting['contingencyBlockHoursRate'] = costingDefaultValues.contingencyBlockHoursRate;

    const newCostingRecord = {
      ...emptyCosting,
      enquiryUuid: record.enquiryUuid,
      commercialOwnerId: record.accountManagerId,
      revenueCalculationMethod: "COST PLUS",
      cargo
    }
    eventBus.trigger('AddCostingDialog.open', newCostingRecord);
  }

  const viewCosting = async (record) => {
    if (record.associatedCostingUuid) {
      const costingRecord = await getCosting(record.associatedCostingUuid);
      if (costingRecord) {
        // eventBus.trigger("EditCostingDialog.open", costingRecord);
        eventBus.trigger("ViewCostingDialog.open", costingRecord);
      }
    }
  }
  const editCosting = async (record) => {
    if (record.associatedCostingUuid) {
      const costingRecord = await getCosting(record.associatedCostingUuid);
      if (costingRecord) {
        eventBus.trigger("EditCostingDialog.open", costingRecord);
      }
    }
  }

  const getOfferPrice = async (associatedCostingUuid) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/costings/list/${associatedCostingUuid}`);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const costingRecord = response.data[0];
        return costingRecord.totalRevenue;
      }

      if (!response.data) {
        const message = `Could not retrieve Costing Information. Server is not responding`
        console.log(message);
        SystemData.alert("Error", message);
      } else if (!response.data.success) {
        const message = `Could not retrieve Costing Information. Server returned ${response.data.error}`;
          console.log(message);
        SystemData.alert("Error", message);
      }
    } catch (err) {
      const message = `Could not retrieve Costing Information. Server is not responding`;
      console.log(message);
      SystemData.alert("Error", message);
    }

  }

  const getCargoData = async (enquiryUuid) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/enquirycargo/list/${enquiryUuid}`);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data;
      }

      if (!response.data) {
        const message = `Could not retrieve Enquiry Cargo. Server is not responding`
        console.log(message);
        SystemData.alert("Error", message);
      } else if (!response.data.success) {
        const message = `Could not retrieve Enquiry Cargo. Server returned ${response.data.error}`;
        console.log(message);
        SystemData.alert("Error", message);
      }

      return null;
    } catch (err) {
      const message = `Could not retrieve Enquiry Cargo. Server is not responding`;
      console.log(message);
      SystemData.alert("Error", message);
    }
  }


  const prepareQuoteEmail = async (rowData, defaults, template, additionalData, slrReview = false) => {
    let exclusions = [];
    let subjectTo = [];
    if (additionalData) {
      exclusions = await getQuoteTerms("QUOTE EXCLUSIONS", additionalData);
      subjectTo = await getQuoteTerms("QUOTE SUBJECT TO", additionalData);
    }

    const operatorName = __.startCase(__.toLower(process.env.REACT_APP_COMPANY)) || "Magma";
    let subject = `${operatorName} Aviation Quote // ${rowData.associatedQuoteReference ? `Reference ${rowData.associatedQuoteReference} // ` : ""}${rowData.cargoDetails} // ${moment(rowData.cargoAvailability).format("DD MMM, YYYY") ?? ""}`;
    if (rowData.replySubject && rowData.replySubject !== "") {
      subject = rowData.replySubject;
    }
    const userInfo = __.find(departmentUsers?.data, {user_id: rowData.accountManagerId});
    const commercialManager = userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : "";
    const totalRevenue = await getOfferPrice(rowData.associatedCostingUuid) || "";
    const enquiryCargo = await getCargoData(rowData.enquiryUuid);
    const emailRecord = {
      recipients: rowData.contactEmail,
      carbonCopy: __.has(defaults, "notify") ? defaults.notify : "",
      subject: subject,
      templateType: "SALES QUOTE",
      templateUuid: __.has(defaults, template) ? defaults[template] : "",
      contactName: rowData.contactName ?? "Valued Customer", //TODO: required
      companyName: rowData.companyName,
      enquiryDate: moment(rowData.enquiryDate).format("DD/MMM"),
      commercialManager: commercialManager,
      quoteReference: rowData.associatedQuoteReference,
      totalRevenue: totalRevenue !== "" ? new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3 }).format(totalRevenue) : "",
      repeatData: enquiryCargo ? [enquiryCargo, exclusions, subjectTo] : [[], exclusions, subjectTo],
    }
    if (__.has(defaults, "emailFrom") && defaults.emailFrom !== "") {
      emailRecord["from"] = defaults.emailFrom;
    }

    return emailRecord;
  }

  const sendQuoteEmail = async (emailFunction, rowData, template, companyName, dialogTitle, additionalData) => {
    if (defaultValues && template) {
      const emailRecord = await emailFunction(rowData, defaultValues, template, additionalData);
      SystemData.sendEmail(emailRecord, `${!dialogTitle?.toLowerCase().includes("send") ? "Send " : ""}${dialogTitle??""} ${!dialogTitle?"from":"to"} ${companyName}`, true);
    }
  }

  const generateQuote = async (emailFunction, rowData, template, companyName, dialogTitle, additionalData, costingUuid) => {
    const extraKeys = [];
    handleAddQuote.mutate({
      extraKeys,
      record: {...rowData, quoteTemplate: template, payloadType: additionalData, costingUuid},
      postSuccess: (result) => {
        const record = result.record;
        if (record.data) {
          if (__.has(record.data, "success") && record.data.success) {
            queryClient.invalidateQueries(["Enquiries"]);
            SystemData.confirm(
              "Quote Generated",
              "Do you want to email the new quote to the enquiry originator?",
              [
                {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
                {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
              ],
              async (response) => {
                if (response) {
                  await sendQuoteEmail(emailFunction, rowData, template, companyName, dialogTitle, additionalData);
                }
              }
            );
          } else {
            if (record.data.error) {
              SystemData.alert("Error", `${record.data.error}`);
            } else {
              SystemData.alert("Error", `An Unexpected Error has occurred generating a Quote.`);
            }
          }
        } else {
          SystemData.alert("Error", `An Unexpected Error has occurred generating a Quote.`);
        }
      },
      postError: (data) => {
        if (data.errorMessage) {
          SystemData.alert("Error", `${data.errorMessage}`);
        } else {
          SystemData.alert("Error", `An Unexpected Error has occurred.`);
        }
      }
    });
  }

  const executeListAction = async (params) => {
    if (!params.record || !params.selectedOption)
      return;
    if (params?.selectedAction.startsWith("auth0|")) {
      params.record["accountManagerId"] = params?.selectedAction;
      updateEnquiryRecord(params.record, params.selectedAction);
      return;
    }
    if (!__.find(listActionOptions, {action: params.selectedOption})) {
      if (!isNaN(Number(params?.selectedAction))) {
        params.record["orderFirmness"] = params?.selectedAction;
      } else {
        params.record["status"] = params?.selectedAction;
      }
      updateEnquiryRecord(params.record, params.selectedAction);
      return;
    }
    if (params) {
      switch (params.selectedOption) {
        case "Generate Trip":
          await generateTrip(params.record);
          break;
        case "Generate Contract":
          await generateContract(params.record, false);
          break;
        case "Generate Contract with Fuel Annex":
          await generateContract(params.record, true);
          break;
        case "Generate New Costing":
          await generateCosting(params.record);
          break;
        case "Associate Costing":
          await attachCosting(params.record);
          break;
        case "View Current Costing":
          await viewCosting(params.record);
          break;
        case "Edit Current Costing":
          await editCosting(params.record);
          break;
        case "Generate Quote":
          setSelectTemplate({...selectTemplate, open: true, record: params.record});
          break;
        case "Resend Quote":
          await sendQuoteEmail(prepareQuoteEmail, params.record, params?.record?.quoteTemplate || "quoteTemplate", params?.record?.companyName,"Price Quotation", params?.record?.payloadType);
          break;
        case "Follow-up Email":
          await sendQuoteEmail(prepareQuoteEmail, params.record, "quoteFollowupTemplate", params?.record?.companyName,"Followup of Price Quotation", params?.record?.payloadType);
          break;
        case "Further Qualification":
          break;
        default:
          break;
      }
    }
  }

  const handleEditEnquiry = async (rowData) => {
    const record = {cargo:[], ...rowData};
    eventBus.trigger("EditEnquiryDialog.open", record)
  }

  const getColumns = (props) => {
    //const validatorColumn = ValidatorColumn(EnquiriesValidator, true);

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: true
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      //{...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(120, 8, undefined, undefined, undefined, undefined, false),
        title: "Enquiry Source",
        field: "enquirySource",
        initialEditValue: "",
        editComponent: (props) => (
          <ComboSelect
            id="enquirySource"
            group="ENQUIRIES"
            subGroup="SOURCES"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            id="enquirySource"
            group="ENQUIRIES"
            subGroup="SOURCES"
            fullWidth
            value={rowData.enquirySource}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(110, undefined, undefined, undefined, undefined, undefined, false),
        title: "Enquiry Date",
        field: "enquiryDate",
        type: "date",
        emptyValue: initialDate,
        initialEditValue:initialDate,
        editable: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(150, undefined, undefined, undefined, undefined, undefined, false),
        title: "Details",
        field: "cargoDetails",
        editable: false,
      },
      {
        ...baseColumn,
        title: "Company Name",
        field: "companyName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(150, undefined, undefined, undefined, undefined, undefined, false),
        title: "Contact Name",
        field: "contactName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(110, undefined, undefined, undefined, undefined, undefined, false),
        title: "Available at Origin",
        field: "cargoAvailability",
        editComponent: (props) => (
            <DatePicker
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        ),
        render: (rowData) => (
          <div>{rowData.cargoAvailability ? moment.utc(rowData.cargoAvailability).format(dateFormat) : ""}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(110, undefined, undefined, undefined, undefined, undefined, false),
        title: "Required at Destination",
        field: "requiredAtDestination",
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <div>{rowData.requiredAtDestination ? moment.utc(rowData.requiredAtDestination).format(dateFormat) : ""}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(140, 10, 110, undefined, undefined, undefined, false),
        title: "Quote Reference",
        field: "associatedQuoteReference",
      },
      {
        ...baseColumn,
        ...ColumnWidth(150, undefined, undefined, undefined, undefined, undefined, false),
        title: "Account Manager",
        field: "accountManagerId",
        initialEditValue: "",
        editComponent: (props) => (
          <FormSelect
            id="accountManagerId"
            options={userOptions}
            onChange={props.onChange}
            value={props.value}
          />
        ),
        render: (rowData) => {
          const userInfo = __.find(departmentUsers?.data, {user_id: rowData.accountManagerId});
          return (userInfo ? <span>{`${userInfo.firstName} ${userInfo.lastName}`}</span> : <></>)
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(150, undefined, 110, undefined, undefined, undefined, false),
        title: "Status",
        field: "status",
        initialEditValue: "",
        editComponent: (props) => (
          <ComboSelect
            id="enquiryStatus"
            group="ENQUIRIES"
            subGroup="STATUS"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            id="enquiryStatus"
            group="ENQUIRIES"
            subGroup="STATUS"
            fullWidth
            value={rowData.status}
            displayOnly={true}
            style={{fontWeight: "800"}}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, 150, undefined, undefined, undefined, false),
        title: "Next Action Date",
        field: "nextActionDate",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData) => {
          const nextActionMoment = moment(rowData.nextActionDate);
          const style= nextActionMoment.isBefore(moment()) ? {color: "red"} : undefined;
          return ( <span style={style}>{rowData.nextActionDate && nextActionMoment.isValid() ? nextActionMoment.format("DD/MM/YYYY HH:mm") : ""}</span>
          )}
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, 110, undefined, undefined, undefined, false),
        title: "Next Activity",
        field: "nextActivity",
        initialEditValue: "",
        editComponent: (props) => (
          <ComboSelect
            id="nextActivity"
            group="ENQUIRIES"
            subGroup="ACTIVITY"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            id="nextActivity"
            group="ENQUIRIES"
            subGroup="ACTIVITY"
            fullWidth
            value={rowData.nextActivity}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(90, 10, 80, undefined, undefined, undefined, false),
        title: "Qualification",
        field: "orderFirmness",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            suffix={" %"}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            suffix={" %"}
            value={props.orderFirmness}
            decimalScale={0}
          />
        )
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        //icons={{ DetailPanel: React.forwardRef((props, ref) => <Icon>unfold_more</Icon> )}}
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          search: true,
          toolbar: true,
          sorting: true,
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          {
            hidden: false,
            disabled: !state.canEdit,
            icon: () => (
              <IconButton disabled={!state.canEdit}>
                <Icons>add_box</Icons>
              </IconButton>
            ),
            tooltip: "New Enquiry",
            isFreeAction: true,
            onClick: handleNewEnquiry
          },
          (rowData) => (
            {
              hidden: false,
              disabled: false,
              icon: () => {
                const activeMenuOptions = [];
                listActionOptions.forEach(item => {
                  const newItem = {}
                  for (const [key, value] of Object.entries(item)) {
                    newItem[key] = value;
                  }
                  if ( (__.has(item, "prerequisite") && !__.has(rowData, item.prerequisite)) || item.disabledStatuses.includes(rowData.status) ) {
                    newItem["disabled"] = true;
                  }
                  activeMenuOptions.push(newItem);
                })

                // __.remove(activeMenuOptions, function(item) {
                //     return (__.has(item, "prerequisite") && !__.has(rowData, item.prerequisite));
                //   }
                // );

                return(
                  <IconButtonMenu
                    icon={<List/>}
                    size={"small"}
                    offset={-1}
                    disabled={!state.canEdit}
                    style={{ color: state.canEdit ? theme.palette.primary.main : theme.palette.grey[500] }}
                    tooltip={"Enquiry Management Options"}
                    menuItems={activeMenuOptions}
                    //callback={()=> {}}
                    callback={executeListAction}
                    callbackParams={{record: rowData}}
                  />)
              },
              isFreeAction: false,
            }
          ),
          (rowData) => (
            {
              hidden: false,
              icon: 'edit',
              // iconProps: { style: { color: state.canEdit ? 'primary' : theme.palette.grey[500] }},
              tooltip: "Edit Enquiry",
              disabled: !state.canEdit,
              isFreeAction: false,
              onClick: (e, rowData) => {
                handleEditEnquiry(rowData);
              }
            }
          ),
        ]}
        detailPanel={
          [
            {
              tooltip: "Activities",
              icon: 'layers',
              openIcon: 'layers_outlined',
              render: (rowData) => {
                return (
                  <div >
                    <div style={{ backgroundColor: "#f2f2f2" }}>
                      <EnquiryActivityList
                        canEdit={state.canEdit}
                        enquiryUuid={rowData.enquiryUuid}
                        departmentUsers={departmentUsers}
                        userOptions={userOptions}
                        defaultValues={defaultValues}
                      />
                    </div>
                  </div>
                );
              },
            },
          ]
        }
        title="Enquiries"
        columns={getColumns({canEdit: state.canEdit})}
        data={enquiries.data}
        editable={{
          isEditable: false,
          isDeletable: (rowData) => {
            return state.canEdit;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteEnquiry.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />

      <AddEnquiryDialog
        systemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={extraKeys}
        handleAdd={handleAddEnquiry}
        formatChars={formatChars}
        classes={classes}
        canEdit={state.canEdit}
        formMode={"OPERATOR"}
      />

      <EditEnquiryDialog
        systemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={extraKeys}
        handleUpdate={handleUpdateEnquiry}
        formatChars={formatChars}
        classes={classes}
        canEdit={state.canEdit}
      />

      <AddCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={costingDefaultValues}
        classes={costingClasses}
        handleAdd={handleAddCosting}
      />

      <EditCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={costingDefaultValues}
        classes={costingClasses}
        handleUpdate={handleUpdateCosting}
      />

      <ViewCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={costingDefaultValues}
        classes={costingClasses}
      />

      <ViewQuoteDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        classes={costingClasses}
        departmentUsers={departmentUsers}
      />

      <AttachCostingDialog
        SystemData={SystemData}
        classes={classes}
        apiSitePrefix={apiSitePrefix}
      />

      <GenerateTripDialog
        SystemData={SystemData}
        classes={classes}
        apiSitePrefix={apiSitePrefix}
      />

      {selectTemplate.open
        ?
          <TemplateSelectionDialog
            open={selectTemplate.open}
            enquiryUuid={selectTemplate.record.enquiryUuid}
            onChange={(template, templateType, costingUuid)=> {
              const record = __.clone(selectTemplate.record);
              setSelectTemplate({open: false, template: template, templateType: templateType, record: null});
              if (template) {
                generateQuote(prepareQuoteEmail, record, template, record?.companyName,"Price Quotation", templateType, costingUuid).then(()=>{}).catch(error=> {});
              }
            }}
          />
        :
          <></>
      }

    </div>
  );

}

export default EnquiriesList;
